import { Spacing } from '@persona-id/design-tokens';
import { produce } from 'immer';
import { useCallback } from 'react';
import styled from 'styled-components';

import { FieldWrapper } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateEditor/shared';
import HelpIcon from 'dashboard/components-lib/HelpIcon';
import {
  InquiryTemplateVersionCombinedStep,
  InquiryTemplateVersionStep,
} from 'dashboard/models/inquiry-template-version';
import Select, { Option } from 'design-system/components/Select';
import { Label } from 'design-system/components/Text';
import { FieldSchema } from 'lib/fields';
import { UiComponentConfig } from 'lib/interfaces/ui-components';

const ComponentFieldWrapper = styled(FieldWrapper)`
  margin-bottom: ${Spacing[3]};
`;

export interface DeviceHandoffEditSectionProps {
  step: InquiryTemplateVersionCombinedStep;
  fieldSchema: FieldSchema[];
  updateSteps: ({
    updatedStep,
    component,
  }: {
    updatedStep: InquiryTemplateVersionStep;
    component?: UiComponentConfig;
  }) => void;
}

const DeviceHandoffEditSection = ({
  step,
  fieldSchema,
  updateSteps,
}: DeviceHandoffEditSectionProps) => {
  const fieldOptions = fieldSchema.map(({ key, label }) => ({
    label: label,
    value: key,
  }));

  const onEmailFieldKeyUpdate = useCallback(
    (fieldKey: string) => {
      const updatedStep = produce(step, (draftStep: InquiryTemplateVersionCombinedStep) => {
        draftStep.config['field-key-device-handoff-email'] = fieldKey;
      });
      updateSteps({ updatedStep });
    },
    [step, updateSteps],
  );

  const onPhoneNumberFieldKeyUpdate = useCallback(
    (fieldKey: string) => {
      const updatedStep = produce(step, (draftStep: InquiryTemplateVersionCombinedStep) => {
        draftStep.config['field-key-device-handoff-phone-number'] = fieldKey;
      });
      updateSteps({ updatedStep });
    },
    [step, updateSteps],
  );

  return (
    <div>
      <ComponentFieldWrapper>
        <Label>Email field key override</Label>
        <HelpIcon>
          <p>
            If set, this overrides the email field key configured at the template level for this
            step.
          </p>
          <br />
          <p>
            This populates the specified field with the email address used during device handoff if
            the specified field is empty, and prefills the device handoff input value with the field
            value if the specified field is not empty.
          </p>
        </HelpIcon>
        <Select
          size='sm'
          id='email-field-key__select'
          value={step.config['field-key-device-handoff-email']}
          onValueChange={onEmailFieldKeyUpdate}
          placeholder='No field referenced'
        >
          {fieldOptions.map((option) => (
            <Option key={option.value} value={option.value}>
              {option.label}
            </Option>
          ))}
        </Select>
      </ComponentFieldWrapper>
      <ComponentFieldWrapper>
        <Label>Phone number field key override</Label>
        <HelpIcon>
          <p>
            If set, this overrides the phone number field key configured at the template level for
            this step.
          </p>
          <br />
          <p>
            This populates the specified field with the phone number used during device handoff if
            the specified field is empty, and prefills the device handoff input value with the field
            value if the specified field is not empty.
          </p>
        </HelpIcon>
        <Select
          size='sm'
          id='phone-number-field-key__select'
          value={step.config['field-key-device-handoff-phone-number']}
          onValueChange={onPhoneNumberFieldKeyUpdate}
          placeholder='No field referenced'
        >
          {fieldOptions.map((option) => (
            <Option key={option.value} value={option.value}>
              {option.label}
            </Option>
          ))}
        </Select>
      </ComponentFieldWrapper>
    </div>
  );
};

export default DeviceHandoffEditSection;
